import * as React from "react";
import { useNetwork } from "~/config/capacitor/network/hook";
import { useOfflineUploads } from "~/config/offline-storage/useOfflineUploads.hook";
import { captureException } from "~/config/sentry";
import { streamChatClient } from "~/config/stream-chat";
import { StreamChatOfflineDB } from "./offline-db";
import type {
  BroadcastMessage,
  OfflineChannelMessage,
  StreamChannel,
  StreamChatClientExtended,
  UploadedAttachment,
  UseGlobalOfflineQueueReturn,
} from "./types";

let globalIsProcessing = false;
let globalPendingCount = 0;

export function useGlobalOfflineQueue(): UseGlobalOfflineQueueReturn {
  const [isProcessingQueue, setIsProcessingQueue] =
    React.useState(globalIsProcessing);
  const [pendingMessagesCount, setPendingMessagesCount] =
    React.useState(globalPendingCount);

  const { isOffline } = useNetwork();
  const { getStoredFile, markComplete, removeUploadsForMessage } =
    useOfflineUploads();

  const dbRef = React.useRef<StreamChatOfflineDB | null>(null);
  const [isDBInitialized, setIsDBInitialized] = React.useState(false);

  const initDB = React.useCallback(async () => {
    try {
      if (!dbRef.current) {
        dbRef.current = new StreamChatOfflineDB();
        await dbRef.current.init();
      } else {
        try {
          await dbRef.current?.getQueuedMessages();
        } catch (error) {
          await dbRef.current?.init();
        }
      }
      setIsDBInitialized(true);
    } catch (error) {
      captureException(error, {
        tags: { action: "initDB", component: "useGlobalOfflineQueue" },
      });
      setIsDBInitialized(false);
    }
  }, []);

  const updatePendingCount = React.useCallback(async () => {
    if (!isDBInitialized) {
      return;
    }

    try {
      const queuedMessages = await dbRef.current!.getQueuedMessages();
      globalPendingCount = queuedMessages.length;
      setPendingMessagesCount(queuedMessages.length);
    } catch (error) {
      captureException(error, {
        tags: {
          action: "updatePendingCount",
          component: "useGlobalOfflineQueue",
        },
      });
    }
  }, [isDBInitialized]);

  const isStreamChatConnected = React.useCallback((): boolean => {
    const client = streamChatClient as unknown as StreamChatClientExtended;
    const isConnected = Boolean(client?.connected ?? client?.user?.id);
    return isConnected;
  }, []);

  const forceStreamChatReconnect = React.useCallback(async () => {
    try {
      const client = streamChatClient as unknown as StreamChatClientExtended;

      if (
        !client?.connected &&
        client?.user &&
        typeof client.connectUser === "function"
      ) {
        await client.connectUser();
      } else if (typeof client.connect === "function" && !client?.connected) {
        await client.connect();
      }

      await new Promise((resolve) => setTimeout(resolve, 1000));
    } catch (error) {
      captureException(error, {
        tags: {
          action: "forceStreamChatReconnect",
          component: "useGlobalOfflineQueue",
        },
      });
    }
  }, []);

  const uploadAttachments = React.useCallback(
    async (
      channel: StreamChannel,
      messageId: string
    ): Promise<UploadedAttachment[]> => {
      try {
        const channelMessage = channel.state.messages.find(
          (m) => m.id === messageId
        );

        let offlineMessage: OfflineChannelMessage | null = null;
        try {
          const offlineMessages = await dbRef.current!.getChannelMessages(
            channel.cid
          );
          offlineMessage =
            offlineMessages.find(
              (msg: OfflineChannelMessage) => msg.id === messageId
            ) ?? null;
        } catch (error) {
          captureException(error as Error, {
            extra: { context: "get-offline-messages", messageId },
            tags: { component: "useGlobalOfflineQueue" },
          });
        }

        const channelAttachments = channelMessage?.attachments ?? [];
        const offlineAttachments = offlineMessage?.attachments ?? [];

        if (!channelAttachments.length && !offlineAttachments.length) {
          return [];
        }

        const attachmentsToProcess =
          channelAttachments.length > 0
            ? channelAttachments
            : offlineAttachments;

        const uploadedAttachments: UploadedAttachment[] = [];

        for (const attachment of attachmentsToProcess) {
          if (!attachment.uploading) {
            uploadedAttachments.push(attachment);
            continue;
          }

          try {
            const file = await getStoredFile(attachment.id);
            if (!file) {
              captureException(new Error("File not found for upload"), {
                extra: { attachmentId: attachment.id },
                tags: { component: "useGlobalOfflineQueue" },
              });
              continue;
            }

            const { file: uploadedFile } = await channel.sendFile(
              file,
              file.type,
              file.name
            );

            markComplete(attachment.id, uploadedFile);

            const uploadedAttachment = {
              ...attachment,
              asset_url: uploadedFile,
              image_url: file.type.startsWith("image/")
                ? uploadedFile
                : undefined,
              uploading: undefined,
            };

            uploadedAttachments.push(uploadedAttachment);
          } catch (error) {
            captureException(error as Error, {
              extra: {
                attachmentId: attachment.id,
                fileName: attachment.file_name,
                messageId,
              },
              tags: {
                action: "uploadAttachment",
                component: "useGlobalOfflineQueue",
              },
            });
          }
        }

        return uploadedAttachments;
      } catch (error) {
        captureException(error as Error, {
          extra: { context: "upload-attachments", messageId },
          tags: { component: "useGlobalOfflineQueue" },
        });
        return [];
      }
    },
    [getStoredFile, markComplete]
  );

  const processQueue = React.useCallback(async () => {
    if (globalIsProcessing || !isDBInitialized) {
      return;
    }

    globalIsProcessing = true;
    setIsProcessingQueue(true);

    try {
      const queuedMessages = await dbRef.current!.getQueuedMessages();

      queuedMessages.sort((a, b) => {
        return (
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
      });

      for (const queuedMessage of queuedMessages) {
        if (queuedMessage.type !== "send-message") continue;

        try {
          const [channel] = await streamChatClient.queryChannels({
            cid: queuedMessage.cid,
          });

          if (!channel) {
            continue;
          }

          const uploadedAttachments = await uploadAttachments(
            channel as StreamChannel,
            queuedMessage.id
          );

          const messageData = {
            text: queuedMessage.message.text,
            ...(uploadedAttachments.length > 0 && {
              attachments: uploadedAttachments,
            }),
            ...(queuedMessage.message.user && {
              user: queuedMessage.message.user,
            }),
          };

          const response = await channel.sendMessage(messageData);

          const pendingIndex = channel.state.messages.findIndex(
            (m: { id: string }) => m.id === queuedMessage.id
          );
          if (pendingIndex > -1) {
            channel.state.messages.splice(pendingIndex, 1);
          }

          await dbRef.current!.deleteMessage(queuedMessage.id).catch(() => {
            /* ignore */
          });

          await dbRef
            .current!.updateChannelMessageStatus(
              queuedMessage.id,
              "sent",
              false
            )
            .catch(() => {
              /* ignore */
            });

          await dbRef.current!.removeQueuedMessage(queuedMessage.id);

          await removeUploadsForMessage(queuedMessage.id);
        } catch (error) {
          captureException(error, {
            extra: {
              channelCid: queuedMessage.cid,
              messageId: queuedMessage.id,
            },
            tags: {
              action: "processQueuedMessage",
              component: "useGlobalOfflineQueue",
            },
          });
        }
      }

      await updatePendingCount();
    } catch (error) {
      captureException(error, {
        tags: { action: "processQueue", component: "useGlobalOfflineQueue" },
      });
    } finally {
      globalIsProcessing = false;
      setIsProcessingQueue(false);
    }
  }, [
    isDBInitialized,
    updatePendingCount,
    uploadAttachments,
    removeUploadsForMessage,
  ]);

  React.useEffect(() => {
    void initDB();
  }, [initDB]);

  React.useEffect(() => {
    if (isDBInitialized) {
      void updatePendingCount();
    }
  }, [isDBInitialized, updatePendingCount]);

  React.useEffect(() => {
    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel("stream-offline");
      bc.onmessage = (ev) => {
        const data = ev.data as BroadcastMessage;
        if (data?.type === "pending-task-added") {
          void updatePendingCount();
        }
      };
    } catch {
      /* BroadcastChannel not supported */
    }
    return () => {
      if (bc) bc.close();
    };
  }, [updatePendingCount]);

  React.useEffect(() => {
    if (!isOffline && pendingMessagesCount > 0 && isDBInitialized) {
      const reconnectAndProcess = async () => {
        try {
          let attempts = 0;
          const maxAttempts = 15;

          while (!streamChatClient.user && attempts < maxAttempts) {
            await new Promise((r) => setTimeout(r, 1000));
            attempts++;
          }

          if (!streamChatClient.user) {
            return;
          }

          await forceStreamChatReconnect();

          attempts = 0;
          const maxConnAttempts = 10;
          while (attempts < maxConnAttempts) {
            if (isStreamChatConnected()) {
              await processQueue();
              return;
            }
            await new Promise((resolve) => setTimeout(resolve, 1000));
            attempts++;
          }
        } catch (error) {
          captureException(error, {
            tags: {
              action: "reconnectAndProcess",
              component: "useGlobalOfflineQueue",
            },
          });
        }
      };
      void reconnectAndProcess();
    }
  }, [
    isOffline,
    pendingMessagesCount,
    processQueue,
    isDBInitialized,
    forceStreamChatReconnect,
    isStreamChatConnected,
  ]);

  return {
    isProcessingQueue,
    pendingMessagesCount,
  };
}
